import { Link } from "react-router-dom";
import SEOMeta from "@/components/base/SEOMeta";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import HealthDisclaimer from "@/components/base/HealthDisclaimer";

const faqs = [
  {
    q: "Do I have to consider myself an alcoholic to use Quiet Recovery Space?",
    a: "Not at all. Serena's Quiet Recovery Space is for anyone who wants to better understand or change their relationship with alcohol — whether you want to cut back, take a break, explore moderation, or work toward sobriety. There are no labels here, and no requirement to identify in any particular way.",
  },
  {
    q: "What if I simply want to drink less, not stop completely?",
    a: "That is completely welcome here. You set your own goals. Many people who come to Quiet Recovery Space are not aiming for complete sobriety — they want to reduce their drinking, break unhelpful patterns, or simply feel more in control. The coaching and resources support whatever direction feels right for you.",
  },
  {
    q: "Is the service confidential?",
    a: "Yes, absolutely. Your privacy is paramount. All coaching sessions are conducted privately via secure video or messaging platforms. The community space is private and not visible to the public. You may use your first name only or a pseudonym. Nothing is shared outside the programme without your explicit consent.",
  },
  {
    q: "Can I get support online?",
    a: "Yes — all of Serena's services are delivered online. Coaching sessions take place via secure video call, and between-session support is available through text and voice messaging. You can access support from anywhere with an internet connection, from the comfort and privacy of your own home.",
  },
  {
    q: "What happens when I first contact Serena?",
    a: "Your first step is a calm, confidential conversation — usually a 30-minute call — where you can share as much or as little as you feel comfortable. Serena will listen without judgement, ask gentle questions to understand your situation, and together you will explore what kind of support might be most helpful. There is no obligation to continue.",
  },
  {
    q: "Do I need to stop drinking completely?",
    a: "No. The programme is designed around your personal goals. Whether you want to reduce your drinking, take a temporary break, explore alcohol-free living, or work toward long-term sobriety, the approach adapts to you. There is no single 'right' path.",
  },
  {
    q: "Can you help me understand why I drink?",
    a: "Yes — this is a core part of the work. Together, you and Serena explore the emotional triggers, situations, habits, and patterns behind your drinking. Understanding the 'why' is often the most powerful step toward meaningful, lasting change.",
  },
  {
    q: "Is this therapy or medical treatment?",
    a: "No. Serena's Quiet Recovery Space provides supportive coaching and educational resources. It is not therapy, counselling, or medical treatment. If you are physically dependent on alcohol, have a diagnosed mental health condition, or need clinical support, Serena can help you understand your options and, where appropriate, signpost you toward professional services. This programme works well alongside therapy or medical care but does not replace them.",
  },
  {
    q: "What if I need medical help?",
    a: "If you are concerned about alcohol withdrawal, physical dependence, or any medical aspect of your drinking, please speak to your GP or a qualified healthcare professional. Stopping alcohol suddenly can be dangerous for some people. Serena can support you alongside medical care but will always recommend seeking appropriate clinical help when needed.",
  },
  {
    q: "How do I get started?",
    a: "The easiest way is to book a free, no-obligation 30-minute conversation with Serena through the Calendly link on the Contact page, or send a message using the contact form. There is no pressure, no commitment, and no judgement — just a quiet space to explore whether this feels right for you.",
  },
  {
    q: "How is Quiet Recovery Space different from other programmes?",
    a: "Most recovery programmes ask you to label yourself, follow rigid rules, or attend public meetings. Quiet Recovery Space is built on a different philosophy: no labels, no shame, no requirement to call yourself anything you are not comfortable with. It is private, flexible, and built around your life — not the other way around. The approach is compassionate, practical, and grounded in Serena's own lived experience of rebuilding life after loss.",
  },
  {
    q: "What if I have tried to change before and it did not work?",
    a: "That experience is very common — and it does not mean you have failed. Many people try several times before finding an approach that genuinely fits. Quiet Recovery Space is designed to work differently: gentle, sustainable steps rather than dramatic overhauls, and support that adapts to you rather than demanding you fit a programme. Previous attempts are not failures; they are information about what did not work, and that information is valuable.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((faq) => ({
    "@type": "Question",
    name: faq.q,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.a,
    },
  })),
};

export default function FAQ() {
  return (
    <main className="min-h-screen pt-20 md:pt-24">
      <SEOMeta
        title="Alcohol Support FAQs | Serena's Quiet Recovery Space"
        description="Common questions about private alcohol recovery coaching. How it works, confidentiality, whether you need to stop drinking completely, getting started, and more. No judgement, just honest answers."
        canonicalUrl="/faq"
        ogType="website"
        schema={faqSchema}
      />

      <section className="w-full bg-background-50 py-16 md:py-24">
        <div className="w-full px-4 sm:px-6 lg:px-10">
          <div className="max-w-3xl mx-auto">
            <Breadcrumbs
              items={[
                { label: "Home", href: "/" },
                { label: "Frequently Asked Questions" },
              ]}
            />

            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-light text-foreground-950 mb-4">
              Frequently Asked Questions
            </h1>
            <p className="font-body text-sm md:text-base text-foreground-600 mb-10 max-w-2xl leading-relaxed">
              Honest answers to common questions about private alcohol recovery coaching, how it works, and whether it might be right for you.
            </p>

            <div className="space-y-3 mb-12">
              {faqs.map((faq, i) => (
                <details
                  key={i}
                  className="group bg-background-100 rounded-lg border border-background-200/60"
                >
                  <summary className="flex items-center justify-between p-5 cursor-pointer font-body text-sm md:text-base font-medium text-foreground-900 list-none">
                    {faq.q}
                    <span className="w-6 h-6 flex items-center justify-center flex-shrink-0 ml-3 group-open:hidden">
                      <i className="ri-add-line text-foreground-500" />
                    </span>
                    <span className="w-6 h-6 flex items-center justify-center flex-shrink-0 ml-3 hidden group-open:flex">
                      <i className="ri-subtract-line text-foreground-500" />
                    </span>
                  </summary>
                  <div className="px-5 pb-5">
                    <p className="font-body text-sm text-foreground-700 leading-relaxed">
                      {faq.a}
                    </p>
                  </div>
                </details>
              ))}
            </div>

            <HealthDisclaimer variant="box" />

            <div className="mt-12 text-center bg-background-100 rounded-lg p-6 md:p-10">
              <h2 className="font-heading text-xl md:text-2xl font-medium text-foreground-950 mb-3">
                Still have questions?
              </h2>
              <p className="font-body text-sm text-foreground-600 mb-6 max-w-md mx-auto leading-relaxed">
                If you did not find what you were looking for, reach out to Serena directly. Every conversation is private and without obligation.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Link
                  to="/contact"
                  className="inline-block bg-primary-500 text-background-50 font-body text-sm font-medium px-6 py-3 rounded-md hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  Contact Serena
                </Link>
                <a
                  href="https://calendly.com/sraimes43/30min"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="inline-flex items-center gap-2 font-body text-sm text-foreground-700 hover:text-foreground-950 transition-colors duration-300 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-calendar-check-line text-base" />
                  Book a free call
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}